import { useState } from 'react';
import { motion } from 'framer-motion';
import { Factory, Truck, Package, MapPin, X, CalendarIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

const PRODUCTION_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
  { value: 'queued', label: 'Queued', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_production', label: 'In Production', color: 'bg-amber-100 text-amber-700' },
  { value: 'quality_check', label: 'Quality Check', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-red-100 text-red-700' },
];

const FULFILLMENT_STATUSES = {
  shipping: [
    { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
    { value: 'ready', label: 'Ready to Ship', color: 'bg-blue-100 text-blue-700' },
    { value: 'shipped', label: 'Shipped', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'in_transit', label: 'In Transit', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-amber-100 text-amber-700' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  ],
  pickup: [
    { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
    { value: 'ready_for_pickup', label: 'Ready for Pickup', color: 'bg-blue-100 text-blue-700' },
    { value: 'picked_up', label: 'Picked Up', color: 'bg-green-100 text-green-700' },
  ],
  delivery: [
    { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
    { value: 'ready', label: 'Ready', color: 'bg-blue-100 text-blue-700' },
    { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-amber-100 text-amber-700' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  ],
};

const CARRIERS = [
  { value: 'USPS', label: 'USPS' },
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Local', label: 'Local Delivery' },
  { value: 'Other', label: 'Other' },
];

export const TrackingModal = ({ invoice, onClose, onUpdateProduction, onUpdateFulfillment }) => {
  const [activeTab, setActiveTab] = useState('production');
  const [productionStatus, setProductionStatus] = useState(invoice.production_status || 'pending');
  const [productionNotes, setProductionNotes] = useState('');
  const [estimatedCompletion, setEstimatedCompletion] = useState(
    invoice.production_estimated_completion ? new Date(invoice.production_estimated_completion) : null
  );
  
  const [fulfillmentStatus, setFulfillmentStatus] = useState(invoice.fulfillment_status || 'pending');
  const [trackingNumber, setTrackingNumber] = useState(invoice.tracking_number || '');
  const [carrier, setCarrier] = useState(invoice.carrier || 'USPS');
  const [estimatedDelivery, setEstimatedDelivery] = useState(
    invoice.estimated_delivery ? new Date(invoice.estimated_delivery) : null
  );
  const [deliveryNotes, setDeliveryNotes] = useState('');
  
  const [submitting, setSubmitting] = useState(false);

  const fulfillmentType = invoice.fulfillment_type || 'shipping';
  const fulfillmentStatuses = FULFILLMENT_STATUSES[fulfillmentType] || FULFILLMENT_STATUSES.shipping;

  const handleProductionUpdate = async () => {
    setSubmitting(true);
    await onUpdateProduction(invoice.id, productionStatus, productionNotes);
    setSubmitting(false);
  };

  const handleFulfillmentUpdate = async () => {
    setSubmitting(true);
    await onUpdateFulfillment(invoice.id, {
      status: fulfillmentStatus,
      tracking_number: trackingNumber || null,
      carrier: carrier || null,
      estimated_delivery: estimatedDelivery ? format(estimatedDelivery, 'yyyy-MM-dd') : null,
      notes: deliveryNotes || null
    });
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              Order Tracking - {invoice.invoice_number}
            </h2>
            <p className="text-sm text-neutral-500">{invoice.customer_name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200">
          <div className="px-6 flex gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('production')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'production'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              <Factory className="w-4 h-4" />
              Production
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('fulfillment')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'fulfillment'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {fulfillmentType === 'pickup' ? <MapPin className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
              {fulfillmentType === 'pickup' ? 'Pickup' : fulfillmentType === 'delivery' ? 'Delivery' : 'Shipping'}
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'production' ? (
            <div className="space-y-6">
              {/* Current Status */}
              <div className="bg-neutral-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-neutral-600">Current Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    PRODUCTION_STATUSES.find(s => s.value === invoice.production_status)?.color || 'bg-neutral-100'
                  }`}>
                    {PRODUCTION_STATUSES.find(s => s.value === invoice.production_status)?.label || 'Pending'}
                  </span>
                </div>
                {invoice.production_started_at && (
                  <p className="text-xs text-neutral-500">
                    Started: {new Date(invoice.production_started_at).toLocaleDateString()}
                  </p>
                )}
                {invoice.production_completed_at && (
                  <p className="text-xs text-green-600">
                    Completed: {new Date(invoice.production_completed_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Update Form */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Update Production Status</Label>
                  <Select value={productionStatus} onValueChange={setProductionStatus}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCTION_STATUSES.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium">Estimated Completion (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full mt-2 justify-start text-left font-normal",
                          !estimatedCompletion && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {estimatedCompletion ? format(estimatedCompletion, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={estimatedCompletion}
                        onSelect={setEstimatedCompletion}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-sm font-medium">Notes (Optional)</Label>
                  <Textarea
                    value={productionNotes}
                    onChange={(e) => setProductionNotes(e.target.value)}
                    className="mt-2"
                    rows={3}
                    placeholder="Add production notes..."
                  />
                </div>

                <Button 
                  onClick={handleProductionUpdate} 
                  disabled={submitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {submitting ? 'Updating...' : 'Update Production Status'}
                </Button>
              </div>

              {/* Production Log */}
              {invoice.production_log && invoice.production_log.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-sm text-neutral-600 mb-3">Production History</h4>
                  <div className="space-y-2">
                    {invoice.production_log.slice().reverse().map((log, idx) => (
                      <div key={idx} className="text-sm bg-neutral-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{PRODUCTION_STATUSES.find(s => s.value === log.status)?.label || log.status}</span>
                          <span className="text-neutral-500 text-xs">{new Date(log.timestamp).toLocaleString()}</span>
                        </div>
                        {log.notes && <p className="text-neutral-600 mt-1">{log.notes}</p>}
                        <p className="text-xs text-neutral-400 mt-1">by {log.updated_by}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Current Fulfillment Status */}
              <div className="bg-neutral-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-neutral-600">Current Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    fulfillmentStatuses.find(s => s.value === invoice.fulfillment_status)?.color || 'bg-neutral-100'
                  }`}>
                    {fulfillmentStatuses.find(s => s.value === invoice.fulfillment_status)?.label || 'Pending'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  {fulfillmentType === 'pickup' ? <MapPin className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                  <span className="capitalize">{fulfillmentType}</span>
                </div>
                {invoice.tracking_number && (
                  <p className="text-sm text-blue-600 mt-2">
                    Tracking: {invoice.tracking_number} ({invoice.carrier || 'USPS'})
                  </p>
                )}
                {invoice.shipped_at && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Shipped: {new Date(invoice.shipped_at).toLocaleDateString()}
                  </p>
                )}
                {invoice.delivered_at && (
                  <p className="text-xs text-green-600">
                    Delivered: {new Date(invoice.delivered_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Update Form */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Update {fulfillmentType === 'pickup' ? 'Pickup' : 'Shipping'} Status</Label>
                  <Select value={fulfillmentStatus} onValueChange={setFulfillmentStatus}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {fulfillmentStatuses.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {fulfillmentType !== 'pickup' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Carrier</Label>
                        <Select value={carrier} onValueChange={setCarrier}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select carrier" />
                          </SelectTrigger>
                          <SelectContent>
                            {CARRIERS.map(c => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Tracking Number</Label>
                        <Input
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          className="mt-2"
                          placeholder="Enter tracking #"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Estimated Delivery</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full mt-2 justify-start text-left font-normal",
                              !estimatedDelivery && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {estimatedDelivery ? format(estimatedDelivery, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={estimatedDelivery}
                            onSelect={setEstimatedDelivery}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </>
                )}

                <div>
                  <Label className="text-sm font-medium">Notes (Optional)</Label>
                  <Textarea
                    value={deliveryNotes}
                    onChange={(e) => setDeliveryNotes(e.target.value)}
                    className="mt-2"
                    rows={3}
                    placeholder={fulfillmentType === 'pickup' ? 'Pickup instructions...' : 'Delivery notes...'}
                  />
                </div>

                <Button 
                  onClick={handleFulfillmentUpdate} 
                  disabled={submitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {submitting ? 'Updating...' : `Update ${fulfillmentType === 'pickup' ? 'Pickup' : 'Shipping'} Status`}
                </Button>
              </div>

              {/* Fulfillment Log */}
              {invoice.fulfillment_log && invoice.fulfillment_log.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-sm text-neutral-600 mb-3">Fulfillment History</h4>
                  <div className="space-y-2">
                    {invoice.fulfillment_log.slice().reverse().map((log, idx) => (
                      <div key={idx} className="text-sm bg-neutral-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{fulfillmentStatuses.find(s => s.value === log.status)?.label || log.status}</span>
                          <span className="text-neutral-500 text-xs">{new Date(log.timestamp).toLocaleString()}</span>
                        </div>
                        {log.tracking_number && (
                          <p className="text-blue-600 text-xs mt-1">Tracking: {log.tracking_number} ({log.carrier})</p>
                        )}
                        {log.notes && <p className="text-neutral-600 mt-1">{log.notes}</p>}
                        <p className="text-xs text-neutral-400 mt-1">by {log.updated_by}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TrackingModal;
